import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowUpRight,
  Banknote,
  BarChart3,
  BellRing,
  Building2,
  CheckCircle2,
  Clock3,
  Code2,
  CreditCard,
  KeyRound,
  Link2,
  Moon,
  ReceiptText,
  Route,
  Settings,
  ShieldCheck,
  Smartphone,
  Store,
  Sun,
  Wallet,
  Webhook,
} from 'lucide-react';
import { Button } from './components/Button.jsx';
import {
  AdminKeyPanel,
  BlockerRow,
  DataTable,
  EmptyState,
  MetricCard,
  SectionCard,
  SkeletonScreen,
  StatusBadge,
  cx,
  inputClass,
  labelClass,
} from './components/PlatformPrimitives.jsx';
import { adminGet, adminPost, loadStoredAdminKey, storeAdminKey } from './lib/api.js';
import { asCount, asCurrency, safeArray, titleize, truncateMiddle } from './lib/formatters.js';
import './styles.css';

const views = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3, description: 'Volume, balances, blockers' },
  { id: 'payments', label: 'Payments', icon: CreditCard, description: 'Transactions, timelines, rails' },
  { id: 'checkout', label: 'Checkout', icon: Link2, description: 'Payment links and sessions' },
  { id: 'merchants', label: 'Merchants', icon: Store, description: 'Onboarding and eligibility' },
  { id: 'balances', label: 'Balances', icon: Wallet, description: 'Settlement, reserves, payouts' },
  { id: 'reconciliation', label: 'Reconciliation', icon: ReceiptText, description: 'Ledger, exceptions, exports' },
  { id: 'provider-readiness', label: 'Readiness', icon: ShieldCheck, description: 'Rails, providers, go-live blockers' },
  { id: 'terminal', label: 'Terminal', icon: Smartphone, description: 'Mobile POS and sessions' },
  { id: 'developers', label: 'Developers', icon: Code2, description: 'Webhooks, API keys, events' },
  { id: 'operations', label: 'Operations', icon: BellRing, description: 'Delivery, drift, demo lab' },
  { id: 'settings', label: 'Settings', icon: Settings, description: 'Environment and controls' },
];

const viewIds = views.map((v) => v.id);

const defaultMetrics = {
  grossVolume: '$42,870.50',
  payments: '128',
  failed: '6',
  available: '$31,204.18',
  reserve: '$4,922.40',
  webhooks: '99.8%',
};

const recentPayments = [
  { id: 'pi_8C7F2A', merchant: 'Northstar Dispensary', method: 'Card debit', amount: '$184.20', status: 'Captured', next: 'Included in next settlement batch' },
  { id: 'pi_5B19DD', merchant: 'Harbor Wellness', method: 'BTC Payments', amount: '$92.10 USD equiv', status: 'Confirming', next: 'Waiting for block confirmations' },
  { id: 'pi_44A90C', merchant: 'Evergreen Retail', method: 'USDC stablecoin', amount: '$1,240.00', status: 'Matched', next: 'Reconciliation check passed' },
  { id: 'pi_11AF03', merchant: 'Peak Delivery', method: 'Mock card', amount: '$63.70', status: 'Failed', next: 'Ask customer for another method' },
];

const settlementRows = [
  { label: 'Available balance', value: '$31,204.18', detail: 'Ready for payout batch creation' },
  { label: 'Pending settlement', value: '$6,743.92', detail: 'Captures not yet released' },
  { label: 'Held reserve', value: '$4,922.40', detail: 'Risk reserve visible to merchant' },
  { label: 'Next payout window', value: 'T+2', detail: 'Sandbox schedule; live rails gated' },
];

const merchantRows = [
  { name: 'Northstar Dispensary', profile: 'US cannabis retail', status: 'Eligible', rails: 'Debit, BTC, USDC' },
  { name: 'Harbor Wellness', profile: 'High-risk wellness', status: 'Review', rails: 'BTC, USDC' },
  { name: 'Evergreen Retail', profile: 'US cannabis ecommerce', status: 'Eligible', rails: 'Debit, USDC' },
];

function normalizeView(raw) {
  const clean = (raw || '').replace(/^\/+|\/+$/g, '');
  if (!clean) return 'dashboard';
  if (clean.startsWith('app')) {
    const segment = clean.split('/')[1];
    if (segment && viewIds.includes(segment)) return segment;
  }
  const fromHash = clean.startsWith('#') ? clean.slice(1) : clean;
  return viewIds.includes(fromHash) ? fromHash : 'dashboard';
}

function parseCurrentView() {
  const byPath = normalizeView(window.location.pathname);
  if (byPath !== 'dashboard') return byPath;
  return normalizeView(window.location.hash);
}

function usePlatformData() {
  const [adminKey, setAdminKeyState] = useState('');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastRequestId, setLastRequestId] = useState('');

  useEffect(() => setAdminKeyState(loadStoredAdminKey()), []);

  const setAdminKey = (value) => {
    setAdminKeyState(value);
    if (!value) {
      storeAdminKey('');
      setData({});
      setLastRequestId('');
    }
  };

  const loadAll = async () => {
    if (!adminKey) {
      setError('Enter the admin key to load platform data.');
      return;
    }
    try {
      setError('');
      setLoading(true);
      const [summaryRes, paymentsRes, readinessRes, capabilityRes] = await Promise.all([
        adminGet('/v1/admin/summary', adminKey),
        adminGet('/v1/admin/payments', adminKey),
        adminGet('/v1/admin/provider-readiness-report', adminKey),
        adminGet('/v1/admin/provider-capability-matrix', adminKey),
      ]);
      const payments = safeArray(paymentsRes.payload?.payments);
      const merchantIds = [...new Set(payments.map((payment) => payment.merchantId).filter(Boolean))];
      const merchants = merchantIds.map((merchantId) => ({ id: merchantId, name: merchantId, status: 'Active', industry: 'Restricted commerce' }));
      const selectedMerchantId = merchantIds[0];
      const optionalGet = async (path) => {
        try {
          return await adminGet(path, adminKey);
        } catch {
          return null;
        }
      };
      const money = selectedMerchantId ? await Promise.all([
        optionalGet(`/v1/admin/balances?merchantId=${encodeURIComponent(selectedMerchantId)}&currency=USD`),
        optionalGet(`/v1/admin/payout-batches?merchantId=${encodeURIComponent(selectedMerchantId)}&currency=USD`),
        optionalGet(`/v1/admin/reserve-releases?merchantId=${encodeURIComponent(selectedMerchantId)}&currency=USD`),
        optionalGet(`/v1/admin/settlement-readiness?merchantId=${encodeURIComponent(selectedMerchantId)}`),
        optionalGet(`/v1/admin/reconciliation?merchantId=${encodeURIComponent(selectedMerchantId)}`),
        optionalGet(`/v1/admin/reconciliation/exceptions?merchantId=${encodeURIComponent(selectedMerchantId)}`),
        optionalGet(`/v1/admin/reconciliation/accounting-exceptions?merchantId=${encodeURIComponent(selectedMerchantId)}&currency=USD`),
      ]) : [];
      setData({
        loaded: true,
        summary: summaryRes.payload,
        payments,
        merchants,
        readiness: readinessRes.payload,
        capabilityMatrix: capabilityRes.payload,
        selectedMerchantId,
        balance: money[0]?.payload?.balance,
        payoutBatches: safeArray(money[1]?.payload?.batches),
        reserveReleases: safeArray(money[2]?.payload?.releases),
        settlementReadiness: safeArray(money[3]?.payload?.settlements),
        reconciliationRows: safeArray(money[4]?.payload?.rows),
        reconciliationExceptions: safeArray(money[5]?.payload?.exceptions),
        accountingExceptions: money[6]?.payload,
      });
      setLastRequestId(summaryRes.requestId || paymentsRes.requestId || readinessRes.requestId || '');
      storeAdminKey(adminKey);
    } catch (err) {
      setError(err.message || 'Unable to load Atlas platform data.');
    } finally {
      setLoading(false);
    }
  };

  return { adminKey, setAdminKey, data, loading, error, lastRequestId, onLoad: loadAll };
}

function cents(value) {
  return Number.isFinite(Number(value)) ? Number(value) : 0;
}

function PlatformShell({ activeView, setActiveView, theme, toggleTheme, children }) {
  const active = views.find((view) => view.id === activeView) || views[0];
  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto grid min-h-screen max-w-[1600px] lg:grid-cols-[300px_1fr]">
        <aside className="min-w-0 overflow-hidden border-b border-slate-200 bg-white px-4 py-5 dark:border-slate-800 dark:bg-slate-900 lg:min-h-screen lg:border-b-0 lg:border-r lg:px-5">
          <div className="flex items-center gap-3 px-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950">
              <Banknote className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-black tracking-tight">Atlas</div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Payments Platform</div>
            </div>
          </div>

          <nav className="mt-6 flex flex-wrap gap-2 pb-1 lg:mt-8 lg:grid lg:pb-0" aria-label="Primary">
            {views.map((item) => {
              const Icon = item.icon;
              const selected = activeView === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveView(item.id)}
                  className={cx(
                    'group flex min-w-[9.5rem] items-center gap-3 rounded-2xl px-3 py-3 text-left transition lg:min-w-0',
                    selected ? 'bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                  )}
                  aria-current={selected ? 'page' : undefined}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold">{item.label}</span>
                    <span className={cx('block truncate text-xs', selected ? 'text-slate-300 dark:text-slate-600' : 'text-slate-400 dark:text-slate-500')}>{item.description}</span>
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30">
            <div className="flex items-center gap-2 text-sm font-black text-amber-800 dark:text-amber-300"><AlertTriangle className="h-4 w-4" /> Sandbox mode</div>
            <p className="mt-2 text-xs leading-5 text-amber-700 dark:text-amber-200">Live rails, underwriting, bank settlement, and external delivery stay explicitly gated until real providers are approved.</p>
          </div>
        </aside>

        <main className="min-w-0 px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
          <header className="flex flex-col gap-4 border-b border-slate-200 pb-6 dark:border-slate-800 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge tone="blue">Restricted commerce</StatusBadge>
                <StatusBadge tone="amber">Sandbox</StatusBadge>
              </div>
              <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950 dark:text-white sm:text-4xl">{active.label}</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">Modern payment infrastructure for merchants legacy processors restrict, starting with cannabis and high-risk commerce.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" type="button" onClick={() => setActiveView('provider-readiness')}>Provider readiness <ArrowUpRight className="h-4 w-4" /></Button>
              <Button asChild><a href="/checkout/demo">Checkout preview <ArrowUpRight className="h-4 w-4" /></a></Button>
              <Button variant="secondary" type="button" onClick={toggleTheme} aria-label="Toggle light and dark mode">
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </Button>
            </div>
          </header>

          <div className="py-6">{children}</div>
        </main>
      </div>
    </div>
  );
}

function DashboardScreen({ platform }) {
  const payments = safeArray(platform.data.payments);
  const capturedTotal = payments.filter((payment) => ['captured', 'settled', 'matched'].includes(String(payment.status || '').toLowerCase())).reduce((total, payment) => total + cents(payment.amount), 0);
  const failedCount = payments.filter((payment) => String(payment.status || '').toLowerCase().includes('fail')).length;
  const metrics = useMemo(() => ({
    grossVolume: platform.data.loaded && payments.length ? asCurrency(capturedTotal) : defaultMetrics.grossVolume,
    payments: platform.data.summary?.payments !== undefined ? asCount(platform.data.summary.payments) : String(payments.length || defaultMetrics.payments),
    failed: platform.data.loaded ? asCount(failedCount) : defaultMetrics.failed,
    available: platform.data.balance?.availableBalance !== undefined ? asCurrency(platform.data.balance.availableBalance) : defaultMetrics.available,
    reserve: platform.data.balance?.reserveBalance !== undefined ? asCurrency(platform.data.balance.reserveBalance) : defaultMetrics.reserve,
    webhooks: platform.data.summary?.webhookDeliveries ? `${platform.data.summary.webhookDeliveries} delivered` : defaultMetrics.webhooks,
  }), [platform.data, payments, capturedTotal, failedCount]);

  return (
    <div className="grid gap-5">
      <AdminKeyPanel adminKey={platform.adminKey} setAdminKey={platform.setAdminKey} onLoad={platform.onLoad} loading={platform.loading} error={platform.error} />
      {platform.lastRequestId ? <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Last API request: {platform.lastRequestId}</p> : null}
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Gross volume" value={metrics.grossVolume} detail="Captured and pending processor volume across eligible rails." icon={BarChart3} />
        <MetricCard label="Payments" value={metrics.payments} detail="Payment intents with full lifecycle visibility." icon={CreditCard} tone="emerald" />
        <MetricCard label="Available balance" value={metrics.available} detail="Merchant-visible funds available for payout planning." icon={Wallet} tone="blue" />
        <MetricCard label="Webhook health" value={metrics.webhooks} detail="Delivery visibility for developer integrations." icon={Webhook} tone="slate" />
      </section>

      <section className="grid gap-5 xl:grid-cols-[1.4fr_0.9fr]">
        <SectionCard eyebrow="Money movement" title="Payment activity" description="Every payment answers what happened, why, and what happens next.">
          <div className="grid gap-3">
            {(platform.data.loaded && payments.length ? payments.slice(0, 6).map((payment) => normalizePayment(payment, platform.data.merchants)) : recentPayments).map((payment) => <PaymentRow key={payment.id} payment={payment} />)}
          </div>
        </SectionCard>
        <SectionCard eyebrow="Go-live blockers" title="What keeps this safe" description="Atlas does not pretend sandbox rails are production banking.">
          <div className="grid gap-3">
            <BlockerRow tone="amber" icon={ShieldCheck} title="Provider underwriting" body="Live payment providers and sponsor-bank paths remain gated." />
            <BlockerRow tone="blue" icon={Route} title="Rail routing" body="Card, BTC, stablecoin, and local rails stay explicit per merchant." />
            <BlockerRow tone="emerald" icon={CheckCircle2} title="Ledger proof" body="Ledger balance and reconciliation evidence are visible before payout." />
          </div>
        </SectionCard>
      </section>
    </div>
  );
}

function normalizePayment(payment, merchants = []) {
  const merchantMap = new Map(safeArray(merchants).map((merchant) => [merchant.id, merchant.name || merchant.id]));
  const status = titleize(payment.status || 'pending');
  return {
    id: payment.id,
    merchant: merchantMap.get(payment.merchantId) || payment.merchantId || 'Unknown merchant',
    method: titleize(payment.rail || payment.method || 'payment rail'),
    amount: asCurrency(payment.amount, payment.currency || 'USD'),
    status,
    next: payment.railReference ? `Rail reference ${payment.railReference}` : payment.updatedAt ? `Updated ${new Date(payment.updatedAt).toLocaleString()}` : 'Lifecycle evidence available in detail view',
  };
}

function PaymentRow({ payment }) {
  const tone = payment.status === 'Failed' ? 'rose' : payment.status === 'Confirming' ? 'amber' : 'emerald';
  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950 md:flex-row md:items-center md:justify-between">
      <div>
        <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs font-bold text-slate-500 dark:text-slate-400">{payment.id}</span><StatusBadge tone={tone}>{payment.status}</StatusBadge></div>
        <p className="mt-2 font-bold text-slate-950 dark:text-white">{payment.merchant}</p>
        <p className="text-sm text-slate-500 dark:text-slate-400">{payment.method} · {payment.next}</p>
      </div>
      <div className="text-xl font-black text-slate-950 dark:text-white">{payment.amount}</div>
    </article>
  );
}

function PaymentsScreen({ platform }) {
  const rows = platform.data.loaded && platform.data.payments?.length ? platform.data.payments.map((payment) => normalizePayment(payment, platform.data.merchants)) : recentPayments;
  const selectedPayment = platform.data.payments?.[0];
  return (
    <div className="grid gap-5">
      <SectionCard eyebrow="Payments" title="Transaction lifecycle" description="A processor UI needs payment state, route evidence, settlement impact, and next action — not raw API links.">
        {!platform.data.loaded ? <p className="mb-4 text-sm font-semibold text-amber-700 dark:text-amber-300">Showing sandbox sample rows. Load platform data from Dashboard to use /v1/admin/payments.</p> : null}
        <DataTable
          columns={['ID', 'Merchant', 'Method', 'Amount', 'Status', 'Next']}
          rows={rows.map((row) => ({ id: row.id, merchant: row.merchant, method: row.method, amount: row.amount, status: <StatusBadge tone={row.status === 'Failed' ? 'rose' : row.status === 'Confirming' ? 'amber' : 'emerald'}>{row.status}</StatusBadge>, next: row.next }))}
        />
      </SectionCard>
      <PaymentDetailPanel payment={selectedPayment} fallback={rows[0]} reconciliationRows={platform.data.reconciliationRows} exceptions={platform.data.reconciliationExceptions} />
      <section className="grid gap-4 lg:grid-cols-3">
        <MetricCard label="Authorized" value="$13,420.80" detail="Approved but not yet captured." icon={Clock3} tone="amber" />
        <MetricCard label="Captured" value="$42,870.50" detail="Gross captured processor volume." icon={CheckCircle2} tone="emerald" />
        <MetricCard label="Failed" value="6" detail="Actionable customer or rail failures." icon={AlertTriangle} tone="amber" />
      </section>
    </div>
  );
}

function PaymentDetailPanel({ payment, fallback, reconciliationRows = [], exceptions = [] }) {
  const normalized = payment ? normalizePayment(payment, []) : fallback;
  const rail = titleize(payment?.rail || normalized?.method || 'payment rail');
  const status = titleize(payment?.status || normalized?.status || 'pending');
  const recon = safeArray(reconciliationRows).find((row) => row.paymentIntentId === payment?.id) || safeArray(reconciliationRows)[0];
  const exception = safeArray(exceptions).find((row) => row.paymentIntentId === payment?.id) || safeArray(exceptions)[0];
  const timeline = [
    { label: 'Created', body: payment?.createdAt ? new Date(payment.createdAt).toLocaleString() : 'Payment intent created in sandbox' },
    { label: 'Rail prepared', body: payment?.railReference ? `Reference ${truncateMiddle(payment.railReference, 10)}` : `${rail} route selected` },
    { label: 'Current state', body: `${status}; settlement evidence remains visible to operator` },
    { label: 'Reconciliation', body: recon ? `${recon.linked ? 'Linked' : 'Needs review'} · ${titleize(recon.rail || rail)}` : 'No reconciliation exception found for selected payment' },
  ];
  return (
    <SectionCard eyebrow="Payment detail" title="Lifecycle evidence" description="The selected payment view shows route evidence, state timeline, settlement context, and reconciliation posture before any money movement claim.">
      <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-black text-slate-700 dark:text-slate-200">{normalized?.id || 'pi_sandbox'}</span>
            <StatusBadge tone={String(status).toLowerCase().includes('fail') ? 'rose' : String(status).toLowerCase().includes('pending') ? 'amber' : 'emerald'}>{status}</StatusBadge>
          </div>
          <dl className="mt-5 grid gap-3 text-sm">
            <div><dt className="font-black text-slate-950 dark:text-white">Merchant</dt><dd className="mt-1 text-slate-500 dark:text-slate-400">{normalized?.merchant || payment?.merchantId || 'Selected merchant'}</dd></div>
            <div><dt className="font-black text-slate-950 dark:text-white">Rail</dt><dd className="mt-1 text-slate-500 dark:text-slate-400">{rail}</dd></div>
            <div><dt className="font-black text-slate-950 dark:text-white">Amount</dt><dd className="mt-1 text-slate-500 dark:text-slate-400">{normalized?.amount || asCurrency(payment?.amount, payment?.currency)}</dd></div>
            <div><dt className="font-black text-slate-950 dark:text-white">Request evidence</dt><dd className="mt-1 text-slate-500 dark:text-slate-400">{payment?.railReference ? truncateMiddle(payment.railReference, 14) : 'Route evidence appears after live data load'}</dd></div>
          </dl>
        </div>
        <div className="grid gap-3">
          {timeline.map((item, index) => (
            <div key={item.label} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-black text-blue-700 dark:bg-blue-950 dark:text-blue-300">{index + 1}</div>
              <div><p className="font-bold text-slate-950 dark:text-white">{item.label}</p><p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">{item.body}</p></div>
            </div>
          ))}
          {exception ? <BlockerRow tone="amber" icon={AlertTriangle} title="Exception watch" body={`${titleize(exception.type)} · ${exception.action || 'Operator review recommended'}`} /> : null}
        </div>
      </div>
    </SectionCard>
  );
}

function CheckoutScreen({ platform }) {
  const defaultMerchantId = platform.data.selectedMerchantId || platform.data.merchants?.[0]?.id || '';
  const [form, setForm] = useState({
    merchantId: defaultMerchantId,
    locationId: '',
    amount: '42.00',
    rail: 'card_mock',
    externalOrderId: `ATLAS-DEMO-${Date.now().toString().slice(-6)}`,
    orderName: '#ATLAS-DEMO',
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!form.merchantId && defaultMerchantId) setForm((current) => ({ ...current, merchantId: defaultMerchantId }));
  }, [defaultMerchantId, form.merchantId]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const createOrder = async () => {
    if (!platform.adminKey) {
      setError('Enter the admin key on Dashboard before creating checkout sessions.');
      return;
    }
    if (!form.merchantId) {
      setError('Merchant ID is required. Create or load a merchant first.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const centsAmount = Math.round(Number(form.amount) * 100);
      const { payload, requestId } = await adminPost('/v1/external-orders', {
        merchantId: form.merchantId,
        locationId: form.locationId || undefined,
        storefront: 'generic',
        externalOrderId: form.externalOrderId,
        orderName: form.orderName,
        amount: centsAmount,
        currency: 'USD',
        rail: form.rail,
        cardType: form.rail === 'card_mock' ? 'debit' : undefined,
        productCategories: ['legal_cannabis'],
        fulfillmentCountry: 'US',
        fulfillmentRegion: 'CA',
        callbackUrl: 'https://merchant.example.invalid/atlas/callback',
        items: [{ sku: 'ATLAS-DEMO-SKU', quantity: 1, amount: centsAmount, category: 'legal_cannabis' }],
        metadata: { createdFrom: 'atlas-ui-checkout-builder' },
      }, platform.adminKey, 'atlas-ui-external-order');
      setResult({ ...payload, requestId });
      setForm((current) => ({ ...current, externalOrderId: `ATLAS-DEMO-${Date.now().toString().slice(-6)}` }));
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || 'Unable to create checkout session.');
    } finally {
      setLoading(false);
    }
  };

  const checkoutUrl = result?.checkoutSession?.checkoutUrl;
  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_0.9fr]">
      <SectionCard eyebrow="Hosted checkout" title="Create a payment link" description="Build a sandbox external order and checkout session without touching live payment rails.">
        <div className="grid gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label className={labelClass}>Merchant ID<input className={inputClass} value={form.merchantId} onChange={update('merchantId')} placeholder="mrc_..." /></label>
            <label className={labelClass}>Location ID optional<input className={inputClass} value={form.locationId} onChange={update('locationId')} placeholder="loc_..." /></label>
            <label className={labelClass}>Amount USD<input className={inputClass} value={form.amount} onChange={update('amount')} inputMode="decimal" /></label>
            <label className={labelClass}>Rail<select className={inputClass} value={form.rail} onChange={update('rail')}><option value="card_mock">Mock debit card</option><option value="bitcoin">BTC Payments</option><option value="stablecoin">USDC stablecoin</option></select></label>
            <label className={labelClass}>External order ID<input className={inputClass} value={form.externalOrderId} onChange={update('externalOrderId')} /></label>
            <label className={labelClass}>Order name<input className={inputClass} value={form.orderName} onChange={update('orderName')} /></label>
          </div>
          <div className={labelClass}>Eligible methods<div className="mt-2 flex flex-wrap gap-2"><StatusBadge tone="emerald">Debit</StatusBadge><StatusBadge tone="blue">BTC</StatusBadge><StatusBadge tone="blue">USDC</StatusBadge><StatusBadge tone="amber">Sandbox only</StatusBadge></div></div>
          {error ? <p className="text-sm font-semibold text-rose-600 dark:text-rose-300">{error}</p> : null}
          <Button type="button" className="w-fit" onClick={createOrder} disabled={loading}>{loading ? 'Creating…' : 'Create sandbox checkout'}</Button>
        </div>
      </SectionCard>
      <SectionCard eyebrow="Customer experience" title="Checkout session result" description="Checkout links show payment status language merchants and customers can understand.">
        {result ? (
          <div className="grid gap-3">
            <BlockerRow tone="emerald" icon={CheckCircle2} title="Checkout created" body={`${result.order?.orderName || result.order?.id} · ${asCurrency(result.payment?.amount, result.payment?.currency)}`} />
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-xs font-black uppercase tracking-wide text-slate-400 dark:text-slate-500">Checkout URL</p>
              <a className="mt-2 inline-flex break-all font-mono text-sm font-bold text-blue-700 hover:underline dark:text-blue-300" href={checkoutUrl} target="_blank" rel="noreferrer">{checkoutUrl}</a>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Request: {result.requestId || 'captured by API client'}</p>
            </div>
            <PaymentRow payment={normalizePayment(result.payment || {}, platform.data.merchants)} />
          </div>
        ) : (
          <div className="grid gap-3">
            {['Pending payment', 'Confirming network payment', 'Captured', 'Underpaid', 'Expired'].map((state) => <div key={state} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">{state}</div>)}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function MerchantsScreen({ platform }) {
  const rows = platform.data.loaded && platform.data.merchants?.length ? platform.data.merchants.map((merchant) => ({ name: merchant.name || merchant.id, profile: merchant.industry || merchant.marketProfileId || 'Restricted commerce', status: merchant.status || 'Active', rails: safeArray(merchant.paymentMethods).join(', ') || merchant.jurisdiction || 'Policy-derived rails' })) : merchantRows;
  const [form, setForm] = useState({ platformName: 'Atlas Demo Platform', merchantName: 'Demo Dispensary', industry: 'legal_cannabis', jurisdiction: 'US-CA', locationName: 'Ecommerce storefront', locationType: 'ecommerce' });
  const [created, setCreated] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const createMerchantFlow = async () => {
    if (!platform.adminKey) {
      setError('Enter the admin key on Dashboard before creating sandbox merchants.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const platformRes = await adminPost('/v1/platforms', { name: form.platformName }, platform.adminKey, 'atlas-ui-platform');
      const merchantRes = await adminPost('/v1/merchants', { platformId: platformRes.payload.id, name: form.merchantName, industry: form.industry, jurisdiction: form.jurisdiction }, platform.adminKey, 'atlas-ui-merchant');
      const locationRes = await adminPost(`/v1/merchants/${merchantRes.payload.id}/locations`, { name: form.locationName, type: form.locationType }, platform.adminKey, 'atlas-ui-location');
      setCreated({ platform: platformRes.payload, merchant: merchantRes.payload, location: locationRes.payload, requestId: locationRes.requestId || merchantRes.requestId || platformRes.requestId });
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || 'Unable to create merchant.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-5">
      <SectionCard eyebrow="Merchant onboarding" title="Restricted commerce merchants" description="Atlas should onboard merchants like a real processor: business profile, locations, market profile, rail eligibility, and risk status.">
        {!platform.data.loaded ? <p className="mb-4 text-sm font-semibold text-amber-700 dark:text-amber-300">Showing sandbox sample merchants. Load platform data from Dashboard to use live platform rows.</p> : null}
        <DataTable columns={['Name', 'Profile', 'Status', 'Rails']} rows={rows.map((row) => ({ ...row, status: <StatusBadge tone={String(row.status).toLowerCase().includes('eligible') || String(row.status).toLowerCase().includes('active') ? 'emerald' : 'amber'}>{titleize(row.status)}</StatusBadge> }))} />
      </SectionCard>
      <section className="grid gap-5 xl:grid-cols-[1fr_0.9fr]">
        <SectionCard eyebrow="Onboarding builder" title="Create sandbox merchant" description="Create a platform, merchant, and ecommerce location as one sandbox onboarding flow. No live underwriting or bank movement is implied.">
          <div className="grid gap-4 md:grid-cols-2">
            <label className={labelClass}>Platform name<input className={inputClass} value={form.platformName} onChange={update('platformName')} /></label>
            <label className={labelClass}>Merchant name<input className={inputClass} value={form.merchantName} onChange={update('merchantName')} /></label>
            <label className={labelClass}>Industry<select className={inputClass} value={form.industry} onChange={update('industry')}><option value="legal_cannabis">Legal cannabis</option><option value="high_risk_ecommerce">High-risk ecommerce</option></select></label>
            <label className={labelClass}>Jurisdiction<input className={inputClass} value={form.jurisdiction} onChange={update('jurisdiction')} placeholder="US-CA" /></label>
            <label className={labelClass}>Location name<input className={inputClass} value={form.locationName} onChange={update('locationName')} /></label>
            <label className={labelClass}>Location type<select className={inputClass} value={form.locationType} onChange={update('locationType')}><option value="ecommerce">Ecommerce</option><option value="retail">Retail</option><option value="delivery">Delivery</option></select></label>
          </div>
          {error ? <p className="mt-4 text-sm font-semibold text-rose-600 dark:text-rose-300">{error}</p> : null}
          <Button type="button" className="mt-4" onClick={createMerchantFlow} disabled={loading}>{loading ? 'Creating…' : 'Create sandbox merchant'}</Button>
        </SectionCard>
        <SectionCard eyebrow="Created records" title="Onboarding result" description="Use these generated IDs in Checkout, Terminal, Balances, and Reconciliation flows.">
          {created ? <div className="grid gap-3">
            <BlockerRow tone="emerald" icon={Building2} title="Platform" body={`${created.platform.name} · ${created.platform.id}`} />
            <BlockerRow tone="emerald" icon={Store} title="Merchant" body={`${created.merchant.name} · ${created.merchant.id}`} />
            <BlockerRow tone="blue" icon={Link2} title="Location" body={`${created.location.name} · ${created.location.id}`} />
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Last request: {created.requestId || 'captured by API client'}</p>
          </div> : <EmptyState title="No merchant created yet" body="Create a sandbox merchant to generate reusable IDs for checkout/session testing." />}
        </SectionCard>
      </section>
      <section className="grid gap-4 lg:grid-cols-3">
        <MetricCard label="Active merchants" value={platform.data.loaded ? asCount(rows.length) : "3"} detail="Sandbox merchant records ready for UI workflows." icon={Building2} />
        <MetricCard label="Locations" value="5" detail="Retail, delivery, and ecommerce locations." icon={Store} tone="emerald" />
        <MetricCard label="Review queue" value="1" detail="Compliance and underwriting placeholder review." icon={ShieldCheck} tone="amber" />
      </section>
    </div>
  );
}

function BalancesScreen({ platform }) {
  const balanceRows = platform.data.balance ? [
    { label: 'Available balance', value: asCurrency(platform.data.balance.availableBalance, platform.data.balance.currency), detail: 'Ready for payout planning' },
    { label: 'Captured gross', value: asCurrency(platform.data.balance.grossCaptured, platform.data.balance.currency), detail: 'Gross captured balance for selected merchant' },
    { label: 'Held reserve', value: asCurrency(platform.data.balance.reserveBalance, platform.data.balance.currency), detail: 'Risk reserve visible to merchant' },
    { label: 'Payout batches', value: asCount(platform.data.payoutBatches?.length), detail: 'Created payout batches for selected merchant' },
  ] : settlementRows;
  return (
    <div className="grid gap-5">
      <SectionCard eyebrow="Settlement" title="Balances and payouts" description="Merchants need to understand gross, fees, reserves, available balance, payout timing, and blockers without calling support.">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {balanceRows.map((row) => <MetricCard key={row.label} label={row.label} value={row.value} detail={row.detail} icon={Wallet} tone={row.label.includes('Held') ? 'amber' : 'blue'} />)}
        </div>
      </SectionCard>
      <SectionCard eyebrow="Payout lifecycle" title="Next payout batch" description="Payout creation remains visible but live money movement stays approval/provider gated.">
        <div className="grid gap-3 md:grid-cols-4">
          {['Collected', 'Fees calculated', 'Reserve held', 'Ready for payout'].map((step, index) => <div key={step} className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"><div className="text-xs font-black uppercase tracking-wide text-slate-400 dark:text-slate-500">Step {index + 1}</div><div className="mt-2 font-bold text-slate-950 dark:text-white">{step}</div></div>)}
        </div>
      </SectionCard>
    </div>
  );
}

function ReconciliationScreen({ platform }) {
  const rows = safeArray(platform.data.reconciliationRows);
  const exceptions = safeArray(platform.data.reconciliationExceptions);
  const accounting = platform.data.accountingExceptions;
  const tableRows = rows.length ? rows.slice(0, 8).map((row) => ({
    id: row.paymentIntentId,
    rail: titleize(row.rail),
    amount: asCurrency(row.amount, row.currency || 'USD'),
    status: <StatusBadge tone={row.linked ? 'emerald' : 'amber'}>{row.linked ? 'Linked' : 'Review'}</StatusBadge>,
    evidence: row.externalOrderId || row.stablecoin?.txHash || row.btcpay?.invoiceId || 'Ledger-only evidence',
  })) : [
    { id: 'pi_44A90C', rail: 'USDC', amount: '$1,240.00', status: <StatusBadge tone="emerald">Linked</StatusBadge>, evidence: 'Stablecoin observation matched to payment intent' },
    { id: 'pi_5B19DD', rail: 'BTC Payments', amount: '$92.10', status: <StatusBadge tone="amber">Confirming</StatusBadge>, evidence: 'Invoice webhook pending final settlement evidence' },
  ];
  return (
    <div className="grid gap-5">
      <SectionCard eyebrow="Reconciliation" title="Ledger, exceptions, and accounting export" description="This cockpit turns payment state into operator-trust evidence: ledger linkage, exception queues, settlement readiness, and accounting drift.">
        {!platform.data.loaded ? <p className="mb-4 text-sm font-semibold text-amber-700 dark:text-amber-300">Showing sample reconciliation rows. Load platform data from Dashboard to use /v1/admin/reconciliation.</p> : null}
        <DataTable columns={['ID', 'Rail', 'Amount', 'Status', 'Evidence']} rows={tableRows} />
      </SectionCard>
      <section className="grid gap-4 lg:grid-cols-3">
        <MetricCard label="Ledger status" value={accounting?.ok === false ? 'Review' : 'Balanced'} detail="Accounting drift and ledger balance are visible before payout decisions." icon={CheckCircle2} tone={accounting?.ok === false ? 'amber' : 'emerald'} />
        <MetricCard label="Exception queue" value={asCount(exceptions.length)} detail="Unmatched, underpaid, reorged, and settlement exceptions surface here." icon={AlertTriangle} tone={exceptions.length ? 'amber' : 'emerald'} />
        <MetricCard label="Export posture" value="JSON / CSV" detail="Accounting exports remain audit-ready and read-only from this screen." icon={ReceiptText} tone="blue" />
      </section>
      <SectionCard eyebrow="Exceptions" title="Operator review queue" description="Every exception should have a clear action and a traceable payment or rail identifier.">
        {exceptions.length ? <div className="grid gap-3">{exceptions.slice(0, 5).map((exception, index) => <BlockerRow key={`${exception.type}-${index}`} tone={exception.severity === 'blocked' || exception.severity === 'critical' ? 'rose' : 'amber'} icon={AlertTriangle} title={titleize(exception.type)} body={`${exception.paymentIntentId || exception.invoiceId || exception.txHash || 'No payment id'} · ${exception.action || 'Review required'}`} />)}</div> : <EmptyState title="No open reconciliation exceptions" body="Loaded data has no exception rows for the selected merchant, or the live optional endpoint is unavailable." />}
      </SectionCard>
    </div>
  );
}

function ProviderReadinessScreen({ platform }) {
  const fallbackProviderRows = [
    { provider: 'Atlas sandbox ledger', rail: 'Mock card', status: <StatusBadge tone="emerald">Ready</StatusBadge>, blocker: 'Sandbox only; no live card acquiring' },
    { provider: 'BTC Payments', rail: 'BTC', status: <StatusBadge tone="emerald">Proof ready</StatusBadge>, blocker: 'Local regtest implementation proof; no mainnet funds' },
    { provider: 'Stablecoin watcher', rail: 'USDC', status: <StatusBadge tone="blue">Configured</StatusBadge>, blocker: 'Provider credentials and custody policy required' },
    { provider: 'Sponsor-bank path', rail: 'ACH / payout', status: <StatusBadge tone="amber">Blocked</StatusBadge>, blocker: 'Underwriting and banking partner approval' },
  ];

  const reportRows = safeArray(platform.data.readiness?.rows).map((row) => ({
    provider: row.provider || row.candidate || 'Unknown provider',
    rail: row.rail || row.country || 'multi-rail',
    status: <StatusBadge tone={String(row.status || '').includes('ready') ? 'emerald' : String(row.status || '').includes('blocked') ? 'rose' : 'amber'}>{titleize(row.status || 'review')}</StatusBadge>,
    blocker: row.nextAction || safeArray(row.blockers).join(', ') || row.boundary?.sandboxLiveDistinction || 'Review readiness evidence',
  }));
  const providerRows = platform.data.loaded && reportRows.length ? reportRows : fallbackProviderRows;

  return (
    <div className="grid gap-5">
      <SectionCard eyebrow="Provider readiness" title="Rails and go-live blockers" description="Provider readiness now lives inside the same Atlas platform shell instead of opening a separate white legacy admin page.">
        {!platform.data.loaded ? <p className="mb-4 text-sm font-semibold text-amber-700 dark:text-amber-300">Showing sandbox sample readiness. Load platform data from Dashboard to use /v1/admin/provider-readiness-report.</p> : null}
        <DataTable columns={['Provider', 'Rail', 'Status', 'Blocker']} rows={providerRows} />
      </SectionCard>
      <section className="grid gap-4 lg:grid-cols-3">
        <MetricCard label="Ready proofs" value="3" detail="Local/sandbox rails with verified proof coverage." icon={CheckCircle2} tone="emerald" />
        <MetricCard label="Live blockers" value="2" detail="Underwriting, credentials, and bank settlement remain explicit." icon={AlertTriangle} tone="amber" />
        <MetricCard label="Credential values" value="Hidden" detail="Readiness surfaces show status and references only." icon={KeyRound} tone="slate" />
      </section>
      <SectionCard eyebrow="Operator posture" title="What must happen before live processing" description="Atlas can demo processor workflows today, but live card acquiring, bank payouts, and external delivery remain controlled launch gates.">
        <div className="grid gap-3 md:grid-cols-3">
          <BlockerRow tone="amber" icon={ShieldCheck} title="Underwriting" body="Restricted-commerce merchant review and provider approval are required before production rails." />
          <BlockerRow tone="blue" icon={Route} title="Routing" body="Each merchant needs explicit rail eligibility, fallback behavior, and processor routing evidence." />
          <BlockerRow tone="emerald" icon={Webhook} title="Integration health" body="Webhooks, request IDs, idempotency, and settlement evidence must stay visible in-platform." />
        </div>
      </SectionCard>
    </div>
  );
}

function TerminalScreen({ platform }) {
  const defaultMerchantId = platform.data.selectedMerchantId || platform.data.merchants?.[0]?.id || '';
  const [form, setForm] = useState({
    merchantId: defaultMerchantId,
    locationId: '',
    deviceName: 'Counter iPhone POS',
    deviceType: 'ios',
    credentialName: 'ios-counter-sandbox-credential',
    tokenReference: 'vault://mobile/devices/counter-ios',
    amount: '55.00',
    cardType: 'debit',
  });
  const [flow, setFlow] = useState({ device: null, session: null, payment: null, present: null, capture: null, requestId: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState('');

  useEffect(() => {
    if (!form.merchantId && defaultMerchantId) setForm((current) => ({ ...current, merchantId: defaultMerchantId }));
  }, [defaultMerchantId, form.merchantId]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const guard = () => {
    if (!platform.adminKey) return 'Enter the admin key on Dashboard before creating POS sessions.';
    if (!form.merchantId) return 'Merchant ID is required. Create or load a merchant first.';
    return '';
  };

  const registerDevice = async () => {
    const message = guard();
    if (message) {
      setError(message);
      return null;
    }
    try {
      setLoading('device');
      setError('');
      const { payload, requestId } = await adminPost('/v1/mobile/devices', {
        merchantId: form.merchantId,
        locationId: form.locationId || undefined,
        name: form.deviceName,
        deviceType: form.deviceType,
        platform: form.deviceType,
        credentialName: form.credentialName,
        tokenReference: form.tokenReference || undefined,
        capabilities: ['qr_checkout', 'tap_to_pay_future', 'card_mock'],
        metadata: { registeredFrom: 'atlas-ui-terminal' },
      }, platform.adminKey, 'atlas-ui-mobile-device');
      setFlow((current) => ({ ...current, device: payload, requestId }));
      return payload;
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || 'Unable to register device.');
      return null;
    } finally {
      setLoading('');
    }
  };

  const createSession = async () => {
    const device = flow.device || await registerDevice();
    if (!device) return;
    try {
      setLoading('session');
      setError('');
      const { payload, requestId } = await adminPost('/v1/mobile/pos-sessions', {
        deviceId: device.id,
        locationId: form.locationId || undefined,
        amount: Math.round(Number(form.amount) * 100),
        currency: 'USD',
        rail: 'card_mock',
        cardType: form.cardType,
        entryMode: 'customer_qr',
        metadata: { createdFrom: 'atlas-ui-terminal' },
      }, platform.adminKey, 'atlas-ui-mobile-pos-session');
      setFlow((current) => ({ ...current, device, session: payload.mobileSession, payment: payload.payment, present: { customerDisplayUrl: payload.mobileSession?.customerDisplayUrl, qrPayload: payload.mobileSession?.qrPayload }, requestId }));
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || 'Unable to create POS session.');
    } finally {
      setLoading('');
    }
  };

  const presentCustomerLink = async () => {
    if (!flow.session?.id) return;
    try {
      setLoading('present');
      setError('');
      const { payload, requestId } = await adminGet(`/v1/mobile/pos-sessions/${flow.session.id}/present`, platform.adminKey);
      setFlow((current) => ({ ...current, present: payload, requestId: requestId || current.requestId }));
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || 'Unable to present customer link.');
    } finally {
      setLoading('');
    }
  };

  const simulatePayment = async () => {
    if (!flow.session?.id) return;
    try {
      setLoading('capture');
      setError('');
      const { payload, requestId } = await adminPost(`/v1/mobile/pos-sessions/${flow.session.id}/simulate-customer-payment`, { cardType: form.cardType }, platform.adminKey, 'atlas-ui-mobile-pos-capture');
      setFlow((current) => ({ ...current, capture: payload, payment: payload.payment || current.payment, session: payload.mobileSession || current.session, requestId }));
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || 'Unable to simulate customer payment.');
    } finally {
      setLoading('');
    }
  };

  const sessionStatus = flow.capture?.payment?.status || flow.payment?.status || flow.session?.status || 'not_started';
  return (
    <div className="grid gap-5">
      <section className="grid gap-5 xl:grid-cols-[1fr_0.9fr]">
        <SectionCard eyebrow="Terminal" title="Mobile POS and card-present sessions" description="Register a sandbox POS device, present a customer QR/link checkout, and simulate a card-present capture without live card rails.">
          <div className="grid gap-4 md:grid-cols-2">
            <label className={labelClass}>Merchant ID<input className={inputClass} value={form.merchantId} onChange={update('merchantId')} placeholder="mrc_..." /></label>
            <label className={labelClass}>Location ID optional<input className={inputClass} value={form.locationId} onChange={update('locationId')} placeholder="loc_..." /></label>
            <label className={labelClass}>Device name<input className={inputClass} value={form.deviceName} onChange={update('deviceName')} /></label>
            <label className={labelClass}>Device type<select className={inputClass} value={form.deviceType} onChange={update('deviceType')}><option value="ios">iOS mobile POS</option><option value="android">Android mobile POS</option><option value="mobile_web">Mobile web</option></select></label>
            <label className={labelClass}>Credential name<input className={inputClass} value={form.credentialName} onChange={update('credentialName')} /></label>
            <label className={labelClass}>Token reference only<input className={inputClass} value={form.tokenReference} onChange={update('tokenReference')} placeholder="vault://..." /></label>
            <label className={labelClass}>Amount USD<input className={inputClass} value={form.amount} onChange={update('amount')} inputMode="decimal" /></label>
            <label className={labelClass}>Card type<select className={inputClass} value={form.cardType} onChange={update('cardType')}><option value="debit">Debit</option><option value="credit">Credit</option><option value="prepaid">Prepaid</option></select></label>
          </div>
          {error ? <p className="mt-4 text-sm font-semibold text-rose-600 dark:text-rose-300">{error}</p> : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" onClick={registerDevice} disabled={Boolean(loading)}>{loading === 'device' ? 'Registering…' : 'Register device'}</Button>
            <Button type="button" variant="secondary" onClick={createSession} disabled={Boolean(loading)}>{loading === 'session' ? 'Creating…' : 'Create POS session'}</Button>
            <Button type="button" variant="secondary" onClick={presentCustomerLink} disabled={!flow.session?.id || Boolean(loading)}>{loading === 'present' ? 'Presenting…' : 'Present customer link'}</Button>
            <Button type="button" variant="secondary" onClick={simulatePayment} disabled={!flow.session?.id || Boolean(loading)}>{loading === 'capture' ? 'Capturing…' : 'Simulate present card'}</Button>
          </div>
        </SectionCard>
        <SectionCard eyebrow="Customer presented" title="POS session result" description="The merchant sees device authorization, customer link, QR payload, and capture status in one workflow.">
          <div className="grid gap-3">
            <BlockerRow tone={flow.device ? 'emerald' : 'amber'} icon={Smartphone} title="Device" body={flow.device ? `${flow.device.name} · ${flow.device.id} · ${flow.device.credentialName}` : 'Register a device using reference-only credentials.'} />
            <BlockerRow tone={flow.session ? 'emerald' : 'amber'} icon={Link2} title="Customer link" body={flow.present?.customerDisplayUrl || flow.session?.customerDisplayUrl || 'Create a POS session to present QR/link checkout.'} />
            <BlockerRow tone={String(sessionStatus).includes('captured') || String(sessionStatus).includes('completed') ? 'emerald' : 'blue'} icon={CreditCard} title="Payment status" body={`${titleize(sessionStatus)}${flow.payment?.id ? ` · ${flow.payment.id}` : ''}`} />
            {flow.present?.qrPayload ? <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950"><p className="text-xs font-black uppercase tracking-wide text-slate-400 dark:text-slate-500">QR payload</p><p className="mt-2 break-all font-mono text-sm font-bold text-slate-700 dark:text-slate-200">{flow.present.qrPayload}</p></div> : null}
            {flow.requestId ? <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Last request: {flow.requestId}</p> : null}
          </div>
        </SectionCard>
      </section>
      <section className="grid gap-4 lg:grid-cols-3">
        <MetricCard label="Devices" value={flow.device ? '1 active' : 'Register'} detail="Reference-only device credential model; no token values accepted." icon={Smartphone} tone="blue" />
        <MetricCard label="POS sessions" value={flow.session ? titleize(flow.session.status) : 'Customer QR'} detail="Customer-presented QR/link flow maps to checkout session evidence." icon={Link2} tone="emerald" />
        <MetricCard label="Card present" value={titleize(sessionStatus)} detail="Sandbox card-present simulation, no live acquiring." icon={CreditCard} tone="amber" />
      </section>
    </div>
  );
}

function DevelopersScreen({ platform }) {
  const defaultMerchantId = platform.data.selectedMerchantId || platform.data.merchants?.[0]?.id || '';
  const [form, setForm] = useState({
    merchantId: defaultMerchantId,
    platformId: '',
    scope: 'payments:write payments:read webhooks:write terminal:write ledger:read',
    webhookUrl: 'https://example.invalid/atlas/webhooks',
    webhookEvents: 'payment_intent.created,payment_intent.captured',
  });
  const [apiKeyRecord, setApiKeyRecord] = useState(null);
  const [webhookEndpoint, setWebhookEndpoint] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState('');

  useEffect(() => {
    if (!form.merchantId && defaultMerchantId) setForm((current) => ({ ...current, merchantId: defaultMerchantId }));
  }, [defaultMerchantId, form.merchantId]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const requireAdmin = () => {
    if (!platform.adminKey) return 'Enter the admin key on Dashboard before managing developer settings.';
    return '';
  };

  const createApiKey = async () => {
    const message = requireAdmin();
    if (message) {
      setError(message);
      return;
    }
    try {
      setLoading('api-key');
      setError('');
      const { payload, requestId } = await adminPost('/v1/admin/api-keys', {
        platformId: form.platformId || undefined,
        merchantId: form.merchantId || undefined,
        scope: form.scope,
      }, platform.adminKey, 'atlas-ui-api-key');
      const { plaintext, ...safeRecord } = payload || {};
      setApiKeyRecord({ ...safeRecord, requestId, plaintextSuppressed: Boolean(plaintext) });
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || 'Unable to create API key.');
    } finally {
      setLoading('');
    }
  };

  const registerWebhook = async () => {
    const message = requireAdmin();
    if (message) {
      setError(message);
      return;
    }
    if (!form.merchantId) {
      setError('Merchant ID is required before registering a webhook endpoint.');
      return;
    }
    try {
      setLoading('webhook');
      setError('');
      const events = form.webhookEvents.split(',').map((event) => event.trim()).filter(Boolean);
      const { payload, requestId } = await adminPost('/v1/webhook-endpoints', {
        merchantId: form.merchantId,
        url: form.webhookUrl,
        events: events.length ? events : ['*'],
      }, platform.adminKey, 'atlas-ui-webhook-endpoint');
      setWebhookEndpoint({ ...payload, requestId });
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || 'Unable to register webhook endpoint.');
    } finally {
      setLoading('');
    }
  };

  const loadDeliveries = async () => {
    const message = requireAdmin();
    if (message) {
      setError(message);
      return;
    }
    try {
      setLoading('deliveries');
      setError('');
      const { payload } = await adminGet('/v1/admin/webhook-deliveries', platform.adminKey);
      setDeliveries(safeArray(payload?.deliveries));
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || 'Unable to load webhook deliveries.');
    } finally {
      setLoading('');
    }
  };

  const latestDeliveries = deliveries.slice(-6).reverse().map((delivery) => ({
    id: delivery.id,
    endpoint: truncateMiddle(delivery.endpointId || 'endpoint', 10),
    event: truncateMiddle(delivery.eventId || 'event', 10),
    status: <StatusBadge tone={delivery.status === 'delivered' ? 'emerald' : delivery.status === 'failed' ? 'rose' : 'amber'}>{titleize(delivery.status || 'pending')}</StatusBadge>,
    code: delivery.statusCode || delivery.error || 'pending',
  }));

  return (
    <div className="grid gap-5">
      <section className="grid gap-5 xl:grid-cols-[1fr_0.9fr]">
        <SectionCard eyebrow="Developers" title="Webhooks and API access" description="Developer surfaces show integration health, request IDs, idempotency, and masked credential references only.">
          <div className="grid gap-4 md:grid-cols-2">
            <label className={labelClass}>Merchant ID<input className={inputClass} value={form.merchantId} onChange={update('merchantId')} placeholder="mrc_..." /></label>
            <label className={labelClass}>Platform ID optional<input className={inputClass} value={form.platformId} onChange={update('platformId')} placeholder="plt_..." /></label>
            <label className={`${labelClass} md:col-span-2`}>API key scopes<input className={inputClass} value={form.scope} onChange={update('scope')} /></label>
            <label className={`${labelClass} md:col-span-2`}>Webhook URL reference<input className={inputClass} value={form.webhookUrl} onChange={update('webhookUrl')} /></label>
            <label className={`${labelClass} md:col-span-2`}>Webhook events<input className={inputClass} value={form.webhookEvents} onChange={update('webhookEvents')} /></label>
          </div>
          {error ? <p className="mt-4 text-sm font-semibold text-rose-600 dark:text-rose-300">{error}</p> : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" onClick={createApiKey} disabled={Boolean(loading)}>{loading === 'api-key' ? 'Creating…' : 'Create scoped API key'}</Button>
            <Button type="button" variant="secondary" onClick={registerWebhook} disabled={Boolean(loading)}>{loading === 'webhook' ? 'Registering…' : 'Register webhook endpoint'}</Button>
            <Button type="button" variant="secondary" onClick={loadDeliveries} disabled={Boolean(loading)}>{loading === 'deliveries' ? 'Loading…' : 'Load webhook deliveries'}</Button>
          </div>
        </SectionCard>
        <SectionCard eyebrow="Credential safety" title="Created developer assets" description="Secrets are accepted by the backend when generated, but this UI intentionally never renders token or webhook secret values.">
          <div className="grid gap-3">
            <BlockerRow tone={apiKeyRecord ? 'emerald' : 'amber'} icon={KeyRound} title="API key" body={apiKeyRecord ? `${apiKeyRecord.id} · scopes: ${apiKeyRecord.scope || 'default'} · plaintext suppressed` : 'Create a scoped sandbox API key. Plaintext value will not be displayed.'} />
            <BlockerRow tone={webhookEndpoint ? 'emerald' : 'amber'} icon={Webhook} title="Webhook endpoint" body={webhookEndpoint ? `${webhookEndpoint.id} · ${webhookEndpoint.status} · ${safeArray(webhookEndpoint.events).join(', ') || '*'}` : 'Register a webhook endpoint reference for selected merchant events.'} />
            <BlockerRow tone="blue" icon={ReceiptText} title="Request tracing" body={(apiKeyRecord?.requestId || webhookEndpoint?.requestId) ? `Last request ${apiKeyRecord?.requestId || webhookEndpoint?.requestId}` : 'x-request-id appears after a developer action.'} />
          </div>
        </SectionCard>
      </section>
      <section className="grid gap-4 lg:grid-cols-3">
        <MetricCard label="Idempotency" value="Required" detail="Mutation buttons send stable Idempotency-Key headers." icon={Code2} tone="blue" />
        <MetricCard label="Credential values" value="Hidden" detail="API plaintext and webhook secrets are never rendered." icon={KeyRound} tone="emerald" />
        <MetricCard label="Webhook delivery" value={asCount(deliveries.length)} detail="Delivery attempts remain inspectable from admin evidence." icon={Webhook} tone="amber" />
      </section>
      <SectionCard eyebrow="Delivery evidence" title="Webhook delivery history" description="Operators and developers need delivery attempts, status, event IDs, and failures in one place.">
        {latestDeliveries.length ? <DataTable columns={['ID', 'Endpoint', 'Event', 'Status', 'Code / error']} rows={latestDeliveries} /> : <EmptyState title="No webhook deliveries loaded" body="Load deliveries after creating payments or POS events. Failed sandbox deliveries are still useful evidence." />}
      </SectionCard>
      <SectionCard eyebrow="API posture" title="Processor-grade integration rules" description="These constraints stay visible because trust is part of the product.">
        <div className="grid gap-3 text-sm text-slate-600 dark:text-slate-300 md:grid-cols-2">
          <div className="rounded-xl bg-slate-50 p-3 font-mono dark:bg-slate-950">Idempotency-Key: required on mutations</div>
          <div className="rounded-xl bg-slate-50 p-3 font-mono dark:bg-slate-950">x-request-id: returned on every response</div>
          <div className="rounded-xl bg-slate-50 p-3 font-mono dark:bg-slate-950">webhook signatures: verified before mutation</div>
          <div className="rounded-xl bg-slate-50 p-3 font-mono dark:bg-slate-950">credential values: never rendered</div>
        </div>
      </SectionCard>
    </div>
  );
}

function OperationsScreen({ platform }) {
  const defaultMerchantId = platform.data.selectedMerchantId || platform.data.merchants?.[0]?.id || '';
  const [form, setForm] = useState({
    merchantId: defaultMerchantId,
    country: 'US',
    currency: 'USD',
    destinationId: 'atlas_ops_internal_audit',
    destinationLabel: 'Atlas ops internal audit',
    eventType: 'provider_readiness_drift',
    severity: 'blocked',
    provider: 'global_card_processor_generic',
  });
  const [state, setState] = useState({
    policy: null,
    destination: null,
    dryRun: null,
    approval: null,
    driftStatus: null,
    driftRun: null,
    escalation: null,
    simulator: null,
    requestId: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState('');

  useEffect(() => {
    if (!form.merchantId && defaultMerchantId) setForm((current) => ({ ...current, merchantId: defaultMerchantId }));
  }, [defaultMerchantId, form.merchantId]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const requireAdmin = () => {
    if (!platform.adminKey) return 'Enter the admin key on Dashboard before running operations controls.';
    return '';
  };
  const runAction = async (name, fn) => {
    const message = requireAdmin();
    if (message) {
      setError(message);
      return null;
    }
    try {
      setLoading(name);
      setError('');
      const result = await fn();
      return result;
    } catch (err) {
      setError(err.requestId ? `${err.message} · request ${err.requestId}` : err.message || `Unable to run ${name}.`);
      return null;
    } finally {
      setLoading('');
    }
  };

  const loadPolicy = () => runAction('policy', async () => {
    const { payload, requestId } = await adminGet('/v1/admin/notification-policy', platform.adminKey);
    setState((current) => ({ ...current, policy: payload, requestId }));
    return payload;
  });

  const registerDestination = () => runAction('destination', async () => {
    const { payload, requestId } = await adminPost('/v1/admin/notification-destinations', {
      id: form.destinationId,
      type: 'internal_audit',
      label: form.destinationLabel,
      status: 'active',
      approvalRequired: false,
    }, platform.adminKey, 'atlas-ui-notification-destination');
    setState((current) => ({ ...current, destination: payload, requestId }));
    return payload;
  });

  const dryRunNotification = () => runAction('dry-run', async () => {
    const destination = state.destination || await registerDestination();
    if (!destination) return null;
    const { payload, requestId } = await adminPost('/v1/admin/notifications/dry-run', {
      type: form.eventType,
      severity: form.severity,
      subject: 'Atlas operations dry-run',
      source: 'atlas-ui-operations',
      summary: { merchantId: form.merchantId || 'not-selected', destinationId: destination.id, note: 'UI dry-run; no external delivery' },
    }, platform.adminKey, 'atlas-ui-notification-dry-run');
    setState((current) => ({ ...current, destination, dryRun: payload, requestId }));
    return payload;
  });

  const requestApproval = () => runAction('approval', async () => {
    const dryRun = state.dryRun || await dryRunNotification();
    const routeAttemptId = safeArray(dryRun?.routes).find((route) => route.id)?.id;
    if (!routeAttemptId) throw new Error('Dry-run route attempt is required before requesting delivery approval.');
    const { payload, requestId } = await adminPost('/v1/admin/external-delivery-approvals', {
      routeAttemptId,
      requestedBy: 'atlas-ui-operator',
    }, platform.adminKey, 'atlas-ui-external-delivery-approval');
    setState((current) => ({ ...current, approval: payload, requestId }));
    return payload;
  });

  const runDriftStatus = () => runAction('drift-status', async () => {
    const query = new URLSearchParams({ country: form.country, maxCredentialAgeDays: '30' });
    if (form.merchantId) query.set('merchantId', form.merchantId);
    const { payload, requestId } = await adminGet(`/v1/admin/drift-status?${query.toString()}`, platform.adminKey);
    setState((current) => ({ ...current, driftStatus: payload, requestId }));
    return payload;
  });

  const runDriftMonitor = () => runAction('drift-monitor', async () => {
    // The live Postgres store currently exposes drift status reliably; persisted run storage is gated until the runtime store grows that table.
    // Keep this UI no-send/no-money-movement and avoid surfacing backend 5xx noise to operators.
    const query = new URLSearchParams({ country: form.country, maxCredentialAgeDays: '30' });
    if (form.merchantId) query.set('merchantId', form.merchantId);
    const { payload, requestId } = await adminGet(`/v1/admin/drift-status?${query.toString()}`, platform.adminKey);
    const preview = {
      id: `drift_preview_${Date.now()}`,
      status: payload.status || 'checked',
      severity: payload.severity || payload.alertStatus || 'preview',
      summary: payload.summary || {},
      noMoneyMovement: payload.noMoneyMovement !== false,
      previewOnly: true,
    };
    setState((current) => ({ ...current, driftStatus: payload, driftRun: preview, requestId }));
    return preview;
  });

  const createEscalation = () => runAction('escalation', async () => {
    const { payload, requestId } = await adminPost('/v1/admin/escalations/drift-status', {
      filter: { country: form.country, merchantId: form.merchantId || undefined, maxCredentialAgeDays: 30 },
    }, platform.adminKey, 'atlas-ui-drift-escalation');
    setState((current) => ({ ...current, escalation: payload, requestId }));
    return payload;
  });

  const runSimulator = () => runAction('simulator', async () => {
    const { payload, requestId } = await adminPost('/v1/admin/provider-simulators/run-all', {
      provider: form.provider,
    }, platform.adminKey, 'atlas-ui-provider-simulator');
    setState((current) => ({ ...current, simulator: payload, requestId }));
    return payload;
  });

  const routes = safeArray(state.dryRun?.routes).slice(0, 5).map((route) => ({
    id: truncateMiddle(route.id || route.idempotencyKey || 'route', 14),
    destination: route.destinationId || route.destinationType || 'destination',
    mode: route.mode || (route.noExternalSend ? 'dry_run' : 'pending'),
    payload: truncateMiddle(route.payloadHash || 'hash pending', 18),
    status: <StatusBadge tone={route.noExternalSend !== false ? 'emerald' : 'amber'}>{route.noExternalSend !== false ? 'No send' : 'Review'}</StatusBadge>,
  }));
  const simulatorRows = safeArray(state.simulator?.results || state.simulator?.scenarios).slice(0, 6).map((row) => ({
    scenario: row.scenario || row.name || 'scenario',
    status: <StatusBadge tone={row.ok === false ? 'rose' : 'emerald'}>{row.ok === false ? 'Failed' : 'Passed'}</StatusBadge>,
    result: row.result?.status || row.status || row.result?.blocker || 'completed',
  }));
  const driftSummary = state.driftStatus?.summary || state.driftRun?.summary || {};

  return (
    <div className="grid gap-5">
      <section className="grid gap-5 xl:grid-cols-[1fr_0.9fr]">
        <SectionCard eyebrow="Operations" title="Delivery, drift, escalations, and demo lab" description="Run no-send notification dry-runs, local drift checks, escalation records, and provider simulator scenarios without external delivery or money movement.">
          <div className="grid gap-4 md:grid-cols-2">
            <label className={labelClass}>Merchant ID optional<input className={inputClass} value={form.merchantId} onChange={update('merchantId')} placeholder="mrc_..." /></label>
            <label className={labelClass}>Country<input className={inputClass} value={form.country} onChange={update('country')} /></label>
            <label className={labelClass}>Currency<input className={inputClass} value={form.currency} onChange={update('currency')} /></label>
            <label className={labelClass}>Severity<select className={inputClass} value={form.severity} onChange={update('severity')}><option value="warning">Warning</option><option value="critical">Critical</option><option value="blocked">Blocked</option></select></label>
            <label className={labelClass}>Event type<input className={inputClass} value={form.eventType} onChange={update('eventType')} /></label>
            <label className={labelClass}>Simulator provider<input className={inputClass} value={form.provider} onChange={update('provider')} /></label>
            <label className={`${labelClass} md:col-span-2`}>Destination reference<input className={inputClass} value={form.destinationLabel} onChange={update('destinationLabel')} /></label>
          </div>
          {error ? <p className="mt-4 text-sm font-semibold text-rose-600 dark:text-rose-300">{error}</p> : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" onClick={loadPolicy} disabled={Boolean(loading)}>{loading === 'policy' ? 'Loading…' : 'Load notification policy'}</Button>
            <Button type="button" variant="secondary" onClick={dryRunNotification} disabled={Boolean(loading)}>{loading === 'dry-run' || loading === 'destination' ? 'Running…' : 'Dry-run notification'}</Button>
            <Button type="button" variant="secondary" onClick={requestApproval} disabled={Boolean(loading)}>{loading === 'approval' ? 'Requesting…' : 'Request delivery approval'}</Button>
            <Button type="button" variant="secondary" onClick={runDriftStatus} disabled={Boolean(loading)}>{loading === 'drift-status' ? 'Checking…' : 'Check drift status'}</Button>
            <Button type="button" variant="secondary" onClick={runDriftMonitor} disabled={Boolean(loading)}>{loading === 'drift-monitor' ? 'Checking…' : 'Preview drift monitor'}</Button>
            <Button type="button" variant="secondary" onClick={createEscalation} disabled={Boolean(loading)}>{loading === 'escalation' ? 'Creating…' : 'Create drift escalation'}</Button>
            <Button type="button" variant="secondary" onClick={runSimulator} disabled={Boolean(loading)}>{loading === 'simulator' ? 'Running…' : 'Run simulator suite'}</Button>
          </div>
        </SectionCard>
        <SectionCard eyebrow="Live boundaries" title="Operational safety posture" description="The cockpit is allowed to create local evidence and approvals; real external sends stay gated.">
          <div className="grid gap-3">
            <BlockerRow tone={state.policy?.externalDeliveryEnabled ? 'amber' : 'emerald'} icon={BellRing} title="External delivery" body={state.policy ? `Enabled: ${String(state.policy.externalDeliveryEnabled)} · approvals required` : 'Load policy to confirm no-send boundaries.'} />
            <BlockerRow tone={state.dryRun?.noExternalMessagesSent ? 'emerald' : 'amber'} icon={ShieldCheck} title="Dry-run evidence" body={state.dryRun ? `${safeArray(state.dryRun.routes).length} routes · no external messages: ${String(state.dryRun.noExternalMessagesSent)}` : 'Dry-run notification to render route evidence.'} />
            <BlockerRow tone={state.approval ? 'blue' : 'amber'} icon={KeyRound} title="Approval gate" body={state.approval ? `${state.approval.id} · ${state.approval.status} · payload ${truncateMiddle(state.approval.payloadHash || 'hash', 14)}` : 'Create approval record from a dry-run route attempt.'} />
            <BlockerRow tone="blue" icon={ReceiptText} title="Request tracing" body={state.requestId ? `Last request ${state.requestId}` : 'x-request-id appears after operations actions.'} />
          </div>
        </SectionCard>
      </section>
      <section className="grid gap-4 lg:grid-cols-4">
        <MetricCard label="Notification routes" value={asCount(routes.length)} detail="Rendered in dry-run mode." icon={BellRing} tone="amber" />
        <MetricCard label="Drift status" value={titleize(state.driftRun?.status || state.driftStatus?.status || 'not checked')} detail={state.driftRun?.previewOnly ? `Preview · ${state.driftRun.severity}` : `Severity ${state.driftRun?.severity || state.driftStatus?.severity || 'unknown'}`} icon={AlertTriangle} tone={(state.driftRun?.status || state.driftStatus?.status) === 'ok' ? 'emerald' : 'rose'} />
        <MetricCard label="Escalation" value={state.escalation?.id ? 'Created' : 'Ready'} detail={state.escalation?.alertStatus || 'Local records only.'} icon={ArrowUpRight} tone={state.escalation ? 'rose' : 'blue'} />
        <MetricCard label="Simulator" value={state.simulator?.ok ? 'Passed' : 'Idle'} detail={`${asCount(safeArray(state.simulator?.results || state.simulator?.scenarios).length)} scenarios`} icon={ShieldCheck} tone={state.simulator?.ok ? 'emerald' : 'blue'} />
      </section>
      <section className="grid gap-5 xl:grid-cols-2">
        <SectionCard eyebrow="Notification routing" title="Dry-run route attempts" description="Route attempts include idempotency and payload hashes, not message secrets.">
          {routes.length ? <DataTable columns={['ID', 'Destination', 'Mode', 'Payload hash', 'Status']} rows={routes} /> : <EmptyState title="No dry-run routes yet" body="Run a notification dry-run to populate no-send delivery evidence." />}
        </SectionCard>
        <SectionCard eyebrow="Drift monitor" title="Readiness and accounting drift" description="Local drift checks combine provider readiness and accounting proof health.">
          <div className="grid gap-3 text-sm text-slate-600 dark:text-slate-300 sm:grid-cols-2">
            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950">Readiness blockers: <b>{asCount(driftSummary.readinessBlockers ?? driftSummary.blockers ?? 0)}</b></div>
            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950">Accounting exceptions: <b>{asCount(driftSummary.accountingExceptions ?? 0)}</b></div>
            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950">Warnings: <b>{asCount(driftSummary.readinessWarnings ?? state.driftRun?.warnings?.length ?? 0)}</b></div>
            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950">No money movement: <b>{String(state.driftRun?.noMoneyMovement ?? true)}</b></div>
          </div>
        </SectionCard>
      </section>
      <SectionCard eyebrow="Demo lab" title="Provider simulator scenarios" description="Scenario results prove duplicate webhook, out-of-order event, and signature-failure handling without real provider calls.">
        {simulatorRows.length ? <DataTable columns={['Scenario', 'Status', 'Result']} rows={simulatorRows} /> : <EmptyState title="Simulator not run" body="Run the provider simulator suite to populate scenario evidence." />}
      </SectionCard>
    </div>
  );
}

function SettingsScreen({ theme, toggleTheme }) {
  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_0.9fr]">
      <SectionCard eyebrow="Settings" title="Environment and operator controls" description="Safe local settings for the Atlas sandbox platform shell. Secrets are never displayed here.">
        <div className="grid gap-3">
          <BlockerRow tone="blue" icon={Settings} title="Environment" body="Current service mode is sandbox; live processing remains gated." />
          <BlockerRow tone="emerald" icon={KeyRound} title="Admin key storage" body="Admin key is stored only in this browser's localStorage for sandbox API reads." />
          <EmptyState title="Theme" body={`Current theme: ${theme}. Toggle applies across every /app route.`} action={<Button type="button" variant="secondary" onClick={toggleTheme}>{theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}</Button>} />
        </div>
      </SectionCard>
      <SectionCard eyebrow="Rails" title="Currencies and rails" description="Next pass wires /v1/currencies and /v1/rails here for operator visibility.">
        <div className="flex flex-wrap gap-2">
          <StatusBadge tone="emerald">Mock card</StatusBadge>
          <StatusBadge tone="blue">BTC Payments</StatusBadge>
          <StatusBadge tone="blue">USDC</StatusBadge>
          <StatusBadge tone="amber">ACH gated</StatusBadge>
        </div>
      </SectionCard>
    </div>
  );
}

function ScreenRouter({ activeView, theme, toggleTheme, platform }) {
  const screens = {
    dashboard: <DashboardScreen platform={platform} />,
    payments: <PaymentsScreen platform={platform} />,
    checkout: <CheckoutScreen platform={platform} />,
    merchants: <MerchantsScreen platform={platform} />,
    balances: <BalancesScreen platform={platform} />,
    reconciliation: <ReconciliationScreen platform={platform} />,
    'provider-readiness': <ProviderReadinessScreen platform={platform} />,
    terminal: <TerminalScreen platform={platform} />,
    developers: <DevelopersScreen platform={platform} />,
    operations: <OperationsScreen platform={platform} />,
    settings: <SettingsScreen theme={theme} toggleTheme={toggleTheme} />,
  };
  return screens[activeView] || screens.dashboard;
}

function App() {
  const [activeView, setActiveView] = useState(parseCurrentView());
  const [theme, setTheme] = useState(() => localStorage.getItem('atlas-theme') || 'light');
  const platform = usePlatformData();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('atlas-theme', theme);
  }, [theme]);

  useEffect(() => {
    const syncFromLocation = () => setActiveView(parseCurrentView());
    window.addEventListener('hashchange', syncFromLocation);
    window.addEventListener('popstate', syncFromLocation);
    return () => {
      window.removeEventListener('hashchange', syncFromLocation);
      window.removeEventListener('popstate', syncFromLocation);
    };
  }, []);

  useEffect(() => {
    const route = viewIds.includes(activeView) ? `/app/${activeView}` : '/app/dashboard';
    if (window.location.pathname !== route) window.history.replaceState({}, '', route);
  }, [activeView]);

  const selectView = (viewId) => {
    if (!viewIds.includes(viewId)) return;
    setActiveView(viewId);
  };

  const toggleTheme = () => setTheme((current) => current === 'dark' ? 'light' : 'dark');

  return (
    <PlatformShell activeView={activeView} setActiveView={selectView} theme={theme} toggleTheme={toggleTheme}>
      <ScreenRouter activeView={activeView} theme={theme} toggleTheme={toggleTheme} platform={platform} />
    </PlatformShell>
  );
}

export default App;
